import { useId, useMemo, useState } from 'react';
import { type DilutionField, solveDilution } from '@chemai/engine';
import { useI18n } from '../../i18n';
import { NumberField, SaveButton, Segmented, SimBadge, Stat } from '../../components/ui';
import { progress } from '../../lib/progress';

function Flask({ label, conc, maxConc }: { label: string; conc: number; maxConc: number }) {
  const a = Math.min(0.9, 0.08 + (conc / maxConc) * 0.82);
  const clip = `clip${useId().replace(/:/g, '')}`;
  return (
    <figure className="mini-flask">
      <svg viewBox="0 0 120 150" role="img" aria-label={label}>
        <defs><clipPath id={clip}><path d="M50 10 L50 60 L15 125 Q12 140 28 140 L92 140 Q108 140 105 125 L70 60 L70 10 Z" /></clipPath></defs>
        <g clipPath={`url(#${clip})`}>
          <rect x="0" y="70" width="120" height="80" fill={`rgba(30, 110, 220, ${a.toFixed(3)})`} />
        </g>
        <path d="M50 10 L50 60 L15 125 Q12 140 28 140 L92 140 Q108 140 105 125 L70 60 L70 10" fill="none" className="flask-outline" />
      </svg>
      <figcaption>{label}</figcaption>
    </figure>
  );
}

export function DilutionSim() {
  const { t, num, errorMessage } = useI18n();
  const [solveFor, setSolveFor] = useState<DilutionField>('c2');
  const [vals, setVals] = useState({ c1: 1, v1: 10, c2: 0.1, v2: 100 });

  const result = useMemo(() => {
    try {
      const input = { ...vals, [solveFor]: null };
      return { r: solveDilution(input), error: null as string | null };
    } catch (e) {
      return { r: null, error: errorMessage(e) };
    }
  }, [vals, solveFor, errorMessage]);

  const field = (k: DilutionField, max: number, step: number) => (
    <NumberField
      key={k}
      label={t(`dil.${k}`)}
      value={result.r && solveFor === k ? result.r[k] : vals[k]}
      onChange={(v) => setVals((s) => ({ ...s, [k]: v }))}
      min={k.startsWith('c') ? 0.0001 : 0.1}
      max={max}
      step={step}
      slider={solveFor !== k}
      disabled={solveFor === k}
    />
  );

  return (
    <div className="sim">
      <SimBadge />
      <div className="sim-grid">
        <section className="panel">
          <h3>{t('tit.setup')}</h3>
          <Segmented
            label={t('dil.solveFor')}
            value={solveFor}
            onChange={(k) => {
              if (result.r) setVals({ c1: result.r.c1, v1: result.r.v1, c2: result.r.c2, v2: result.r.v2 });
              setSolveFor(k);
            }}
            options={(['c1', 'v1', 'c2', 'v2'] as DilutionField[]).map((k) => ({ value: k, label: k.toUpperCase().replace('1', '₁').replace('2', '₂') }))}
          />
          {field('c1', 18, 0.05)}
          {field('v1', 250, 1)}
          {field('c2', 18, 0.01)}
          {field('v2', 1000, 5)}
        </section>
        <section className="panel apparatus-panel">
          <h3>{t('lab.equipment')}</h3>
          {result.r && (
            <div className="flask-pair">
              <Flask label={`${t('dil.stock')} · ${num(result.r.c1, 3)} mol/L`} conc={result.r.c1} maxConc={result.r.c1} />
              <span aria-hidden="true" className="arrow">→</span>
              <Flask label={`${t('dil.diluted')} · ${num(result.r.c2, 4)} mol/L`} conc={result.r.c2} maxConc={result.r.c1} />
            </div>
          )}
        </section>
        <section className="panel readouts" aria-live="polite">
          <h3>{t('lab.calculations')}</h3>
          {result.error && <p className="error" role="alert">{result.error}</p>}
          {result.r && (
            <>
              <div className="stats">
                <Stat label={t(`dil.${solveFor}`)} value={num(result.r[solveFor], 4)} unit={solveFor.startsWith('c') ? 'mol/L' : 'mL'} emphasis />
                <Stat label={t('dil.factor')} value={`× ${num(result.r.dilutionFactor, 2)}`} />
                <Stat label={t('dil.water')} value={num(result.r.waterToAdd, 2)} unit="mL" />
                <Stat label={t('dil.solute')} value={num(result.r.soluteMmol, 3)} unit="mmol" />
              </div>
              <ul className="calc">
                <li><code>C₁·V₁ = C₂·V₂</code></li>
                <li><code>{num(result.r.c1, 4)} · {num(result.r.v1, 2)} = {num(result.r.c2, 4)} · {num(result.r.v2, 2)} = {num(result.r.soluteMmol, 3)} mmol</code></li>
              </ul>
              <SaveButton onSave={() => progress.recordExperiment('dilution', { c1: result.r!.c1, v1: result.r!.v1, v2: result.r!.v2, c2: Math.round(result.r!.c2 * 10000) / 10000 })} />
            </>
          )}
        </section>
      </div>
      {result.r && (
        <section className="panel">
          <h3>{t('lab.steps')}</h3>
          <ol className="steps">
            <li>{t('dil.step1', { v1: num(result.r.v1, 2) })}</li>
            <li>{t('dil.step2', { v2: num(result.r.v2, 1) })}</li>
            <li>{t('dil.step3', { w: num(result.r.waterToAdd, 1) })}</li>
            <li>{t('lab.safety.acid')}</li>
          </ol>
        </section>
      )}
    </div>
  );
}
